import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { 
      path: isAuthenticated ? '/user-home' : '/', 
      label: 'Inicio', 
      section: 'home' 
    },
    { path: '/sobre-nosotros', label: 'Información', section: 'about' },
    { path: '/market', label: 'Tienda', section: 'market' },
    { path: '/games', label: 'Videojuegos', section: 'games' },
    { path: '/forum', label: 'Foro', section: 'forum' },
    { path: '/messaging', label: 'Mensajes', section: 'messaging' },
    { path: '/user-home', label: 'Dashboard', section: 'dashboard' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = (item: typeof navItems[0]) => {
    if (location.pathname === '/') {
      // Si estamos en la página principal (HomePageV11), navegar directamente a las páginas
      if (item.path === '/' || (isAuthenticated && item.path === '/user-home')) {
        // Si es "Inicio" (o Dashboard apuntando a user-home), navegar según el estado de autenticación
        if (isAuthenticated) {
          navigate('/user-home');
        } else {
          // Ya estamos en la página de inicio para usuarios no autenticados
          return;
        }
      } else {
        // Para todas las demás secciones, navegar a la página correspondiente
        navigate(item.path);
      }
    } else {
      // Si no estamos en la página principal, la navegación normal funcionará
      navigate(item.path);
    }
  };

  const isItemActive = (item: typeof navItems[0]) => {
    if (location.pathname === '/user-home') {
      // En /user-home, destacar solo "Inicio"
      return item.label === 'Inicio';
    }
    return location.pathname === item.path;
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/30 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16 relative">
            {/* Logo/Brand - Mobile */}
            <div className="md:hidden absolute left-0">
              <Link to="/" className="flex items-center">
                <span className="font-orbitron font-bold text-lg tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-neon-pink">
                  La Costa Digital
                </span>
              </Link>
            </div>

            {/* Navigation Links - Desktop - Centered */}
            <div className="hidden md:flex space-x-8 items-center">
              {navItems.map((item) => (
                <div key={item.path}>
                  {location.pathname === '/' ? (
                    <button
                      onClick={() => handleNavClick(item)}
                      className={`relative px-4 py-2 text-base font-orbitron font-semibold transition-all duration-300 tracking-widest text-white hover:text-cyber-blue cursor-pointer`}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      className={`relative px-4 py-2 text-base font-orbitron font-semibold transition-all duration-300 tracking-widest
                        ${isItemActive(item) ? 'text-cyber-blue' : 'text-white hover:text-cyber-blue'}`}
                    >
                      {item.label}
                      {isItemActive(item) && (
                        <motion.div
                          layoutId="navbar-indicator"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-neon-purple to-cyber-blue"
                          initial={false}
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden absolute right-0">
              <motion.button
                onClick={toggleMobileMenu}
                whileTap={{ scale: 0.95 }}
                className="w-12 h-12 flex items-center justify-center text-white hover:text-cyber-blue transition-colors rounded-lg hover:bg-white/10"
                aria-label="Toggle mobile menu"
              >
                <AnimatePresence mode="wait">
                  {isMobileMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaTimes size={20} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaBars size={20} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                onClick={toggleMobileMenu}
              />

              {/* Menu */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-gradient-to-br from-deep-space/95 to-cosmic-violet/95 backdrop-blur-xl border-l border-cyber-blue/30 z-50 shadow-2xl"
              >
                {/* Mobile Menu Header */}
                <div className="flex items-center justify-between p-6 border-b border-cyber-blue/30">
                  <Link to="/" className="flex items-center" onClick={toggleMobileMenu}>
                    <span className="font-orbitron font-bold text-xl tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-neon-pink">
                      La Costa Digital
                    </span>
                  </Link>
                  <motion.button
                    onClick={toggleMobileMenu}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  >
                    <FaTimes size={18} />
                  </motion.button>
                </div>

                {/* Mobile Navigation Items */}
                <div className="p-6 space-y-4">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {location.pathname === '/' ? (
                        <button
                          onClick={() => {
                            handleNavClick(item);
                            toggleMobileMenu();
                          }}
                          className="block w-full px-4 py-4 text-lg font-orbitron font-semibold transition-all duration-300 rounded-xl text-white hover:bg-white/10 hover:text-cyber-blue text-left"
                        >
                          {item.label}
                        </button>
                      ) : (
                        <Link
                          to={item.path}
                          className={`block w-full px-4 py-4 text-lg font-orbitron font-semibold transition-all duration-300 rounded-xl ${
                            isItemActive(item)
                              ? 'bg-gradient-to-r from-cyber-blue/20 to-neon-pink/20 text-cyber-blue border border-cyber-blue/30'
                              : 'text-white hover:bg-white/10 hover:text-cyber-blue'
                          }`}
                        >
                          {item.label}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Mobile Menu Footer */}
                <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-cyber-blue/30">
                  <div className="text-center">
                    <p className="text-sm text-gray-400 font-space-grotesk">
                      La Costa Digital
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Soluciones Inteligentes de Negocio
                    </p>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.nav>

    </>
  );
};

export default Navbar;
